import React, { useEffect, useState } from 'react'
import type { FileRejection } from 'react-dropzone'

import {
  Button,
  Fieldset,
  Icon,
  Input,
  Loading,
  ModalBox,
  Toasts,
  Upload,
  useModalContext,
} from '@fourthwall/components'
import { ArrowShortDownIcon, ArrowShortUpIcon, UploadIcon } from '@fourthwall/icons'

import { getRejectionErrors } from '../../../../../../../utils/dropzone'
import { useCreateImageResources } from '../../../../hooks/useCreateImageResources'
import * as S from './styled'

type Props = {
  src?: string
  width?: number
  height?: number
  alt?: string
  onConfirm: (src: string, width: number, height: number, alt: string) => void
}

export const ImageUploadModal = ({ src, width, height, alt, onConfirm }: Props) => {
  const modal = useModalContext()
  const { createImageResources, isLoading } = useCreateImageResources()

  const [advancedVisible, setAdvancedVisible] = useState(false)

  const [imageSrc, setImageSrc] = useState(src || '')
  const [imageAlt, setImageAlt] = useState(alt || '')
  const [imageWidth, setImageWidth] = useState(width)
  const [imageHeight, setImageHeight] = useState(height)

  useEffect(() => {
    if (alt) setAdvancedVisible(true)
  }, [])

  const handleImageUpload = (files?: File[]) => {
    if (!files) return

    createImageResources({
      files: [files[0]],
      onSuccess: (images) => {
        const image = new Image()
        image.src = images[0].url

        image.onload = () => {
          setImageWidth(image.width)
          setImageHeight(image.height)
          setImageSrc(images[0].url)
          image.remove()
        }
      },
    })
  }

  const handleDropRejection = (rejections: FileRejection[]) => {
    getRejectionErrors(rejections).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const renderImagePreview = () => {
    if (!imageSrc) return null

    return (
      <S.ImageContainer>
        <S.Image src={imageSrc} width={imageWidth} height={imageHeight} />
      </S.ImageContainer>
    )
  }

  const renderUpload = () => {
    if (imageSrc) return null

    return (
      <S.UploadContainer>
        <Loading isLoading={isLoading}>
          <Upload
            accept={{
              'image/*': [],
            }}
            title="Drop image or browse"
            disabled={isLoading}
            icon={UploadIcon}
            iconSize={20}
            maxSize={10485760}
            orientation="horizontal"
            onFileDrop={handleImageUpload}
            onFileDropRejected={handleDropRejection}
          />
        </Loading>
      </S.UploadContainer>
    )
  }

  return (
    <ModalBox
      title={src ? 'Edit image' : 'Upload image'}
      size="small"
      buttons={[
        <Button label="Cancel" onClick={modal.close} />,
        <Button
          label="Confirm"
          appearance="primary"
          type="submit"
          disabled={isLoading || !imageSrc || !imageWidth || !imageHeight}
          onClick={() => {
            onConfirm(imageSrc!, imageWidth!, imageHeight!, imageAlt!)
            modal.close()
          }}
        />,
      ]}
      onClose={modal.close}
    >
      <Fieldset>
        <Fieldset.Row>
          {renderImagePreview()}
          {renderUpload()}
        </Fieldset.Row>
        <Fieldset.Row>
          <S.AdvancedButton type="button" onClick={() => setAdvancedVisible(!advancedVisible)}>
            Advanced{' '}
            <Icon component={advancedVisible ? ArrowShortUpIcon : ArrowShortDownIcon} height={12} />
          </S.AdvancedButton>
        </Fieldset.Row>
        {advancedVisible && (
          <Fieldset.Row variant="small">
            <Input
              label="Image alt text"
              value={imageAlt}
              fullWidth
              disabled={isLoading}
              suggestion="Used to describe images for screen readers and provide context when images fail to load."
              onChange={(e) => setImageAlt(e.target.value)}
            />
          </Fieldset.Row>
        )}
      </Fieldset>
    </ModalBox>
  )
}
